import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { getDogProfileCompletion } from '@/utils/dogUtils';
import ReactApexChart from 'react-apexcharts';
import Modal from '@/components/Modal';
import useInfinitePosts from '@/hooks/useInfinitePosts';
import PostCard from '@/components/PostCard';
import useBreedInsightsOpenAI from '@/hooks/useBreedInsightsOpenAI';

import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@root/firebase';

import Layout from '@components/Layout';
import Tour from '@components/Tour';
import { dashboardTourSteps } from '@utils/TourSteps/Dashboard';
import { dashboardMenu } from '@utils/Index';

function UserDashboard() {
  const { globalData, globalUser, logout, defaultAvatarUrl } = useAuth();
  const navigate = useNavigate();

  const [dogCount, setDogCount] = useState(0);
  const [questionCount, setQuestionCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [dogs, setDogs] = useState([]);
  const [insightsMap, setInsightsMap] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [postText, setPostText] = useState('');
  const [postFiles, setPostFiles] = useState([]); // File[]
  const [postPreviewUrls, setPostPreviewUrls] = useState([]); // string[]

  const dogBreeds = useMemo(
    () => (dogs || []).map((d) => (d.breed || '').trim()).filter(Boolean),
    [dogs]
  );
  const {
    byBreed,
    loading: insightsLoading,
    err: insightsError,
  } = useBreedInsightsOpenAI(dogBreeds, {
    perBreedLimit: 5,
    refreshMs: 10 * 60 * 1000,
  });

  useEffect(() => {
    // map each dog to a single display line (your current UI expects a string)
    const next = {};
    for (const d of dogs) {
      const list = byBreed[d.breed] || [];
      const top = list[0];
      next[d.id] = top ? top.title || top.body || '' : '';
    }
    setInsightsMap(next);
  }, [dogs, byBreed]);

  const {
    items: feedPosts,
    loading: feedLoading,
    eof,
    sentinelRef,
  } = useInfinitePosts({ pageSize: 10 });

  function resetCreatePost() {
    setPostText('');
    setPostFiles([]);
    setPostPreviewUrls([]);
  }

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!globalUser?.uid) return;

      try {
        const notificationsList = [];

        // 1. Events
        const eventsSnap = await getDocs(
          collection(db, 'users', globalUser.uid, 'events')
        );
        eventsSnap.forEach((doc) => {
          const data = doc.data();
          notificationsList.push({
            type: 'Event',
            title: data.title || 'Untitled Event',
            date: data.start?.toDate?.().toLocaleString() || '',
            category: data.category || '',
          });
        });

        // 2. Notifications
        const notifSnap = await getDocs(
          collection(db, 'users', globalUser.uid, 'notifications')
        );
        notifSnap.forEach((doc) => {
          const data = doc.data();
          notificationsList.push({
            type: 'Notification',
            title: data.notification || 'Notification received',
            date: '', // assume no timestamp here
            category: '',
          });
        });

        // 3. Questions
        const questionsQuery = query(
          collection(db, 'questions'),
          where('userId', '==', globalUser.uid)
        );
        const questionsSnap = await getDocs(questionsQuery);
        questionsSnap.forEach((doc) => {
          const data = doc.data();
          notificationsList.push({
            type: 'Question',
            title: data.text || 'Question submitted',
            date: data.createdAt?.toDate?.().toLocaleString() || '',
            category: data.category || '',
          });
        });

        // Sort by date descending if available
        const sorted = notificationsList.sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });

        setNotifications(sorted);
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    };

    fetchNotifications();
  }, [globalUser]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        if (!globalUser?.uid) return;

        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);
        const timestampStart = Timestamp.fromDate(startOfMonth);

        // DOGS
        const dogsSnap = await getDocs(
          collection(db, 'users', globalUser.uid, 'dogs')
        );
        setDogCount(dogsSnap.size);
        setDogs(dogsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));

        // QUESTIONS
        const questionsQuery = query(
          collection(db, 'questions'),
          where('userId', '==', globalUser.uid),
          where('createdAt', '>=', timestampStart)
        );
        const questionsSnap = await getDocs(questionsQuery);
        setQuestionCount(questionsSnap.size);

        // FAVORITES
        let totalFavorites = 0;
        const topCollections = ['memorials', 'articles', 'advert', 'products'];

        /*for (const top of topCollections) {
          const topDocsSnap = await getDocs(collection(db, top));

          for (const topDoc of topDocsSnap.docs) {
            const secondLevelSnap = await getDocs(
              collection(db, top, topDoc.id)
            );

            for (const secondDoc of secondLevelSnap.docs) {
              const favoriteRef = collection(
                db,
                top,
                topDoc.id,
                secondDoc.id,
                'favorite'
              );
              const favoriteQuery = query(
                favoriteRef,
                where('userId', '==', globalUser.uid),
                where('createdAt', '>=', timestampStart)
              );
              const favoriteSnap = await getDocs(favoriteQuery);
              totalFavorites += favoriteSnap.size;
            }
          }
        }*/

        setFavoritesCount(totalFavorites);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchCounts();
  }, [globalUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Error logging out:', err.message);
    }
  };

  return (
    <>
      {/* TWO-COLUMN LAYOUT */}
      <div className='row'>
        {/* LEFT GRID */}
        <div className='col-lg-8'>
          <div className='row'>
            {/* Welcome */}
            <div className='col-12'>
              <div className='card'>
                <div className='card-body'>
                  <h4 className='mb-2'>
                    Welcome to {globalData.name}'s Client Portal
                  </h4>
                  <h6 className='mb-0 text-muted' id='tour-welcome'>
                    Smartdog is your 24/7 co-pilot for your dog parental needs -
                    daily care tips, health alerts, real people you can ask for
                    help. A place to create memories and enjoy all the
                    compassionate community features for your dog owner needs.
                  </h6>
                </div>
              </div>
            </div>

            <div className='col-lg-5'>
              <div className='col-lg-12'>
                {/* Perks (stats) */}
                <div id='dashboardPerks' className='col-12'>
                  <div className='card'>
                    <div className='card-body'>
                      <div className='row text-center'>
                        <div className='col-4 border-end'>
                          <div className='p-1'>
                            <h5 className='mb-0 fs-20 font-w600'>{dogCount}</h5>
                            <p className='text-muted mb-0'>Dogs</p>
                          </div>
                        </div>
                        <div className='col-4 border-end'>
                          <div className='p-1'>
                            <h5 className='mb-0 fs-20 font-w600'>
                              {questionCount}
                            </h5>
                            <p className='text-muted mb-0'>Questions</p>
                          </div>
                        </div>
                        <div className='col-4'>
                          <div className='p-1'>
                            <h5 className='mb-0 fs-20 font-w600'>
                              {favoritesCount}
                            </h5>
                            <p className='text-muted mb-0'>favorites</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Perks (stats) */}

                {/* AI News  */}
                <div className='col-lg-12'>
                  <div className='card h-100'>
                    <div className='card-body'>
                      <h5 className='card-title mb-3'>Latest News</h5>
                      <div
                        className='mx-n4 px-4'
                        data-simplebar
                        style={{ maxHeight: 390 }}
                      >
                        {dogs.length > 0 ? (
                          dogs.map((dog) => {
                            const completion = getDogProfileCompletion(dog);
                            // ... chartOptions omitted for brevity

                            return (
                              <div
                                className='px-3 py-2 border-bottom'
                                key={dog.id}
                              >
                                {/* content unchanged */}
                                <div className='d-flex align-items-center'>
                                  <div className='flex-shrink-0'>
                                    <img
                                      src={
                                        dog.avatar ||
                                        '/assets/images/default-dog.jpg'
                                      }
                                      className='rounded-circle avatar img-thumbnail'
                                      alt={dog.dogName}
                                      style={{
                                        width: '48px',
                                        height: '48px',
                                        objectFit: 'cover',
                                      }}
                                    />
                                  </div>
                                  <div className='flex-grow-1 ms-3 overflow-hidden text-end'>
                                    <h5 className='font-size-15 mb-1 text-truncate'>
                                      {dog.dogName || 'Unnamed Dog'}
                                    </h5>
                                    <p className='font-size-14 text-muted text-truncate mb-0'>
                                      {dog.breed || 'Breed Unknown'}
                                    </p>
                                  </div>
                                </div>

                                <div
                                  className='progress default-progress mt-2'
                                  style={{ height: '8px' }}
                                >
                                  <div
                                    className='progress-bar progress-animated'
                                    style={{ width: `${completion}%` }}
                                    role='progressbar'
                                  />
                                </div>

                                <div className='d-flex align-items-end mt-1 justify-content-between'>
                                  <span>
                                    <small className='text-primary'>
                                      {completion}
                                    </small>
                                    % of Profile Complete
                                  </span>
                                  <h5 className='mb-0 fs-20 font-w600'>
                                    {completion}
                                  </h5>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <p className='text-muted text-center'>
                            No dogs registered yet.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* End AI Tips  */}
              </div>
            </div>

            {/* Social Media  */}
            <div className='col-lg-7'>
              <div className='col-lg-12'>
                <div className='card h-100'>
                  <div className='card-body' id='tour-social'>
                    <h5 className='card-title d-flex align-items-center gap-3'>
                      <img
                        className='rounded-circle header-profile-user'
                        src={globalData?.avatar || defaultAvatarUrl}
                        alt='Header Avatar'
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                      />
                      <input
                        className='form-control flex-grow-1'
                        placeholder={`What's on your mind, ${
                          globalData?.name || ''
                        }?`}
                        onClick={(e) => {
                          e.preventDefault();
                        }}
                        data-bs-toggle='modal'
                        data-bs-target='#createPost'
                      />
                    </h5>
                  </div>
                </div>
              </div>

              {/* End Social Media  */}

              {/* Create Post Modal */}
              <Modal
                id='createPost'
                title='Create Post'
                onConfirm={async () => {
                  setIsSaving(true);
                  try {
                    // 1) create Firestore post doc (without images to get postId)
                    const { createPost, uploadPostImages } = await import(
                      '@/services/posts'
                    );
                    const user = globalUser;

                    // temporary create to get postId (store no images first)
                    // We'll actually upload images first, get URLs, then create post with images (simpler)
                    // Alternative flow: create, get id, then upload to /posts/{uid}/{postId}/..., then update doc.

                    // upload images first (we need a postId; simplest flow: create postId via addDoc with placeholders)
                    const postId = crypto.randomUUID(); // local temp id for pathing
                    // upload to Storage using user.uid + tempId
                    const { uploadPostImages: uploadFn } = await import(
                      '@/services/posts'
                    );

                    const uploaded = await uploadFn({
                      userId: user.uid,
                      postId,
                      files: postFiles,
                    });

                    // now create the post with final images
                    const realPostId = await createPost({
                      user,
                      text: postText,
                      images: uploaded,
                    });

                    resetCreatePost();
                    // optionally: optimistic prepend in feed list (we’ll handle in Feed loader)
                    // Bootstrap modal closes via your Modal component
                  } catch (e) {
                    console.error('Create post failed:', e);
                  } finally {
                    setIsSaving(false);
                  }
                }}
                confirmText='Upload'
                loading={isSaving}
                loadingText='Uploading...'
                size='lg'
              >
                {/* Modal body (no change to Modal logic) */}
                <div className='mb-3'>
                  <textarea
                    className='form-control'
                    rows={3}
                    placeholder={`What's on your mind, ${
                      globalData?.name || ''
                    }?`}
                    value={postText}
                    onChange={(e) => setPostText(e.target.value)}
                    maxLength={3000}
                  />
                  <div className='form-text text-end'>
                    {postText.length}/3000
                  </div>
                </div>

                {/* Use your existing Upload component if available; fallback to file input */}
                <div className='mb-2'>
                  {/* If your upload component is named differently, tell me and I’ll swap it. */}
                  {/* Example API: <Upload multiple accept="image/*" onChange={(files)=>{...}} /> */}
                  {/* Fallback: */}
                  <input
                    type='file'
                    accept='image/*'
                    multiple
                    className='form-control'
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setPostFiles(files);
                      const urls = files.map((f) => URL.createObjectURL(f));
                      setPostPreviewUrls(urls);
                    }}
                  />
                </div>

                {/* Preview grid (Facebook-style) */}
                {postPreviewUrls.length > 0 && (
                  <div className='d-flex flex-wrap gap-2'>
                    {postPreviewUrls.map((src, idx) => (
                      <div
                        key={idx}
                        style={{
                          width: 120,
                          height: 120,
                          position: 'relative',
                        }}
                      >
                        <img
                          src={src}
                          alt={`preview-${idx}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 6,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </Modal>
              {/* Create Post Modal */}

              {/* Social Media  */}
              <div className='col-lg-12'>
                {feedPosts.map((p) => (
                  <PostCard
                    key={p.id}
                    post={p}
                    currentUser={globalUser}
                    defaultAvatarUrl={defaultAvatarUrl}
                  />
                ))}

                {feedLoading && (
                  <div className='text-center text-muted py-2'>Loading…</div>
                )}

                {/* sentinel for infinite scroll */}
                {!eof && <div ref={sentinelRef} style={{ height: 1 }} />}

                {eof && !feedPosts.length && (
                  <div className='text-muted'>
                    No posts yet. Be the first to share!
                  </div>
                )}
              </div>
              {/* End Social Media  */}
            </div>
          </div>
        </div>

        {/* RIGHT PANE (YOUR DOGS) — independent height/scroll */}
        <div className='col-lg-4'>
          {/* Dogs */}
          <div className='col-lg-12'>
            <div className='card '>
              <div className='card-body'>
                <div className='d-flex align-items-start mb-1'>
                  <div className='flex-grow-1'>
                    <h5 className='card-title'>Your Dogs</h5>
                  </div>
                </div>

                <div
                  className='mx-n4 px-4'
                  data-simplebar
                  style={{ maxHeight: '390px' }}
                  id='tour-dogs'
                >
                  {dogs.length > 0 ? (
                    dogs.map((dog) => {
                      const completion = getDogProfileCompletion(dog);

                      const chartOptions = {
                        chart: {
                          type: 'radialBar',
                          sparkline: { enabled: true },
                        },
                        plotOptions: {
                          radialBar: {
                            hollow: { size: '60%' },
                            track: { background: '#f0f0f0' },
                            dataLabels: {
                              name: { show: false },
                              value: {
                                fontSize: '14px',
                                show: true,
                                color: '#000',
                              },
                            },
                          },
                        },
                        fill: {
                          colors: [
                            completion >= 80
                              ? '#28a745'
                              : completion >= 50
                              ? '#ffc107'
                              : '#dc3545',
                          ],
                        },
                        labels: ['Completion'],
                      };

                      return (
                        <div className='px-3 py-2 border-bottom' key={dog.id}>
                          <div className='d-flex align-items-center'>
                            <div className='flex-shrink-0'>
                              <img
                                src={
                                  dog.avatar || '/assets/images/default-dog.jpg'
                                }
                                className='rounded-circle avatar img-thumbnail'
                                alt={dog.dogName}
                                style={{
                                  width: '48px',
                                  height: '48px',
                                  objectFit: 'cover',
                                }}
                              />
                            </div>
                            <div className='flex-grow-1 ms-3 overflow-hidden text-end'>
                              <h5 className='font-size-15 mb-1 text-truncate'>
                                {dog.dogName || 'Unnamed Dog'}
                              </h5>
                              <p className='font-size-14 text-muted text-truncate mb-0'>
                                {dog.breed || 'Breed Unknown'}
                              </p>
                            </div>
                          </div>

                          <div
                            className='progress default-progress mt-2'
                            style={{ height: '8px' }}
                          >
                            <div
                              className='progress-bar progress-animated'
                              style={{
                                width: `${getDogProfileCompletion(dog)}%`,
                              }}
                              role='progressbar'
                            />
                          </div>

                          <div className='d-flex align-items-end mt-1 justify-content-between'>
                            <span>
                              <small className='text-primary'>
                                {getDogProfileCompletion(dog)}
                              </small>
                              % of Profile Complete
                            </span>
                            <h5 className='mb-0 fs-20 font-w600'>
                              {getDogProfileCompletion(dog)}
                            </h5>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className='text-muted text-center'>
                      No dogs registered yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className='col-lg-12'>
            <div
              className='card'
              style={{ overflow: 'hidden' }}
              id='tour-notifications-site'
            >
              <div className='card-header border-0 pb-0'>
                <h4 className='card-title'>Notifications</h4>
              </div>

              <div className='card-body'>
                <div
                  // the scroller is THIS div — not the whole card
                  className='widget-media'
                  style={{
                    maxHeight: 370, // or a fixed: height: 370
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    overscrollBehavior: 'contain', // prevents parent from grabbing scroll
                  }}
                >
                  <ul className='timeline mb-0'>
                    {notifications.length ? (
                      notifications.map((n, idx) => (
                        <li key={idx}>
                          <div className='timeline-panel d-flex justify-content-between'>
                            <div className='media-body'>
                              <h5 className='mb-1 fs-13'>
                                <strong>{n.type}:</strong> {n.title}
                              </h5>
                              {n.date && (
                                <small className='text-muted d-block'>
                                  {n.date}
                                </small>
                              )}
                            </div>

                            <div className='dropdown ms-2'>
                              <button
                                type='button'
                                className='btn btn-primary light sharp'
                                data-bs-toggle='dropdown'
                              >
                                <svg width='18' height='18' viewBox='0 0 24 24'>
                                  <g
                                    fill='none'
                                    stroke='none'
                                    strokeWidth='1'
                                    fillRule='evenodd'
                                  >
                                    <rect x='0' y='0' width='24' height='24' />
                                    <circle fill='#000' cx='5' cy='12' r='2' />
                                    <circle fill='#000' cx='12' cy='12' r='2' />
                                    <circle fill='#000' cx='19' cy='12' r='2' />
                                  </g>
                                </svg>
                              </button>
                              <div className='dropdown-menu dropdown-menu-end'>
                                <button className='dropdown-item'>Edit</button>
                                <button className='dropdown-item'>
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))
                    ) : (
                      <p className='text-muted text-center mb-0'>
                        No notifications yet.
                      </p>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UserDashboard;
